import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../../components/organisms/PageLayout';
import NewKanbanModal from '../../components/molecules/kanban/NewKanbanModal';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { useUser } from '../../context/UserContext';
import { Briefcase, Calendar, Plus, Mail, Pencil } from 'lucide-react';
import { emitKanbansRefresh, emitSubscriptionRefresh, onKanbansRefresh } from '../../utils/subscriptionEvents';
import { showToast } from '../../utils/toastManager';
import '../../styles/page-layout.css';
import '../../styles/dashboard.css';

interface Kanban {
  uid: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

interface KanbanWithStats extends Kanban {
  totalJobs?: number;
  ownerEmail?: string | null;
}

const breadcrumbItems = [
  { label: 'Dashboard', href: '/dashboard' },
];

const Dashboard: React.FC = () => {
  const [kanbans, setKanbans] = useState<KanbanWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const { userProfile } = useUser();
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [editingBoardName, setEditingBoardName] = useState<string>('');
  const [newBoardModalOpen, setNewBoardModalOpen] = useState(false);

  useEffect(() => {
    document.title = 'OfferFlow | Dashboard';
    let cancelled = false;
    fetchKanbans(() => cancelled);
    return () => { cancelled = true; };
  }, [userProfile]);

  useEffect(() => {
    const unsubscribe = onKanbansRefresh(() => {
      fetchKanbans();
    });
    return unsubscribe;
  }, []);

  const fetchKanbans = async (isCancelled: () => boolean = () => false) => {
    try {
      setLoading(true);
      const data = await api.get<Array<Kanban & { total_jobs?: number }>>(
        API_ENDPOINTS.kanbans.listWithStats,
      );
      if (isCancelled()) return;

      if (Array.isArray(data)) {
        const kanbansWithStats = data.map((kanban) => ({
          ...kanban,
          totalJobs: kanban.total_jobs ?? 0,
          ownerEmail: userProfile?.email,
        }));
        setKanbans(kanbansWithStats);
      }
    } catch (error) {
      if (isCancelled()) return;
      setKanbans([]);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return '';
    }
  };

  const handleEditClick = (e: React.MouseEvent, boardId: string, boardName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingBoardId(boardId);
    setEditingBoardName(boardName);
  };

  const handleSaveName = async (boardId: string) => {
    if (editingBoardName.trim() && editingBoardName.trim() !== kanbans.find(k => k.uid === boardId)?.name) {
      try {
        await api.put(API_ENDPOINTS.kanbans.rename, {
          kanban_uid: boardId,
          name: editingBoardName.trim(),
        });
        
        setKanbans(kanbans.map(k => 
          k.uid === boardId ? { ...k, name: editingBoardName.trim() } : k
        ));
        
        emitKanbansRefresh();
      } catch (error) {
        const originalName = kanbans.find(k => k.uid === boardId)?.name || '';
        setEditingBoardName(originalName);
        showToast('Failed to rename board. Please try again.', 'error');
      }
    }
    setEditingBoardId(null);
    setEditingBoardName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent, boardId: string) => {
    if (e.key === 'Enter') {
      handleSaveName(boardId);
    } else if (e.key === 'Escape') {
      setEditingBoardId(null);
      setEditingBoardName('');
    }
  };

  const handleBlur = (boardId: string) => {
    handleSaveName(boardId);
  };

  const handleCreateBoard = async (name: string) => {
    try {
      await api.post(API_ENDPOINTS.kanbans.create, { name });
      emitKanbansRefresh();
      emitSubscriptionRefresh();
    } catch (error) {
      showToast('Failed to create board. Please try again.', 'error');
    }
  };

  return (
    <PageLayout breadcrumbItems={breadcrumbItems} title="Boards">
      <div className="dashboard__header">
        <h1 className="dashboard__title">My Job Tracking Boards</h1>
      </div>

      {loading ? (
        <div className="dashboard__loading">Loading boards...</div>
      ) : kanbans.length === 0 ? (
        <div className="dashboard__empty">
          <div className="dashboard__empty-icon">
            <Briefcase size={40} />
          </div>
          <h2 className="dashboard__empty-title">No job boards yet</h2>
          <p className="dashboard__empty-text">
            Create your first job tracking board to start organizing your job search
          </p>
          <button 
            className="dashboard__empty-button"
            onClick={() => setNewBoardModalOpen(true)}
          >
            Create Board
          </button>
        </div>
      ) : (
        <div className="dashboard__boards-grid">
          {kanbans.map((kanban) => (
            <Link
              key={kanban.uid}
              to={`/track/board/${kanban.uid}`}
              className="dashboard__board-card"
              onClick={(e) => {
                if (editingBoardId === kanban.uid) {
                  e.preventDefault();
                }
              }}
            >
              <div className="dashboard__board-header">
                <div className="dashboard__board-title-row">
                  {editingBoardId === kanban.uid ? (
                    <input
                      type="text"
                      className="dashboard__board-name-input"
                      value={editingBoardName}
                      onChange={(e) => setEditingBoardName(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, kanban.uid)}
                      onBlur={() => handleBlur(kanban.uid)}
                      autoFocus
                      onClick={(e) => e.preventDefault()}
                    />
                  ) : (
                    <>
                      <h3 className="dashboard__board-name">{kanban.name}</h3>
                      <button
                        className="dashboard__board-edit"
                        onClick={(e) => handleEditClick(e, kanban.uid, kanban.name)}
                        title="Edit board name"
                      >
                        <Pencil size={16} />
                      </button>
                    </>
                  )}
                </div>
                {kanban.ownerEmail && (
                  <div className="dashboard__board-owner">
                    <Mail size={14} />
                    <span>{kanban.ownerEmail}</span>
                  </div>
                )}
              </div>
                  
              <div className="dashboard__board-meta">
                <div className="dashboard__board-stat">
                  <Briefcase size={16} className="dashboard__board-stat-icon" />
                  <span className="dashboard__board-stat-label">Jobs:</span>
                  <span className="dashboard__board-stat-value">
                    {kanban.totalJobs || 0}
                  </span>
                </div>
                    
                {kanban.created_at && (
                  <div className="dashboard__board-date">
                    <Calendar size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    Created {formatDate(kanban.created_at)}
                  </div>
                )}
              </div>
            </Link>
          ))}
              
          <button
            className="dashboard__new-board"
            onClick={() => setNewBoardModalOpen(true)}
          >
            <div className="dashboard__new-board-icon">
              <Plus size={24} />
            </div>
            <span className="dashboard__new-board-text">New Board</span>
          </button>
        </div>
      )}

      <NewKanbanModal
        open={newBoardModalOpen}
        onClose={() => setNewBoardModalOpen(false)}
        onCreate={handleCreateBoard}
      />
    </PageLayout>
  );
};

export default Dashboard;
